'use client'

import { useTransition } from 'react'
import { useRouter } from 'next/navigation'
import { Globe } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useLocale } from 'next-intl'
import { useSession } from 'next-auth/react'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'pt', label: 'Português' },
  { value: 'fr', label: 'Français' },
] as const

interface LanguageSelectorProps {
  /** When true, renders full-width to match sidebar nav items */
  fullWidth?: boolean
  className?: string
}

export function LanguageSelector({ fullWidth, className }: LanguageSelectorProps = {}) {
  const locale = useLocale()
  const router = useRouter()
  const [isPending, startTransition] = useTransition()
  const { data: session } = useSession()

  const setPreferredLanguage = trpc.settings.setPreferredLanguage.useMutation()

  function handleChange(newLocale: string) {
    // Set NEXT_LOCALE cookie (next-intl reads this natively)
    document.cookie = `NEXT_LOCALE=${newLocale};path=/;max-age=${365 * 24 * 60 * 60}`

    // If the user is authenticated, also persist to the database for email locale
    if (session?.user?.id) {
      setPreferredLanguage.mutate({ locale: newLocale })
    }

    startTransition(() => {
      router.refresh()
    })
  }

  return (
    <div className={cn('flex items-center gap-1.5', fullWidth && 'w-full', className)}>
      <Globe className="h-4 w-4 text-zinc-400 shrink-0" aria-hidden="true" />
      <Select value={locale} onValueChange={handleChange} disabled={isPending}>
        <SelectTrigger className={cn(
          'text-sm text-zinc-400 hover:text-zinc-300 gap-1 border-zinc-700 bg-transparent px-3 py-1.5',
          fullWidth ? 'w-full justify-between' : 'w-auto'
        )}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {LANGUAGES.map((lang) => (
            <SelectItem key={lang.value} value={lang.value}>
              {lang.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
